import Calendar from '@/components/calendar/Calendar';
import { Button } from '@/components/common/Button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/common/Tabs';
import { APPOINTMENT_STATUS, DATE_YMD_HMS_12H_FORMAT, DEFAULT_SELECT_PAGE_SIZES, DISPLAY_MOMENT_DATE_TIME_HM, ROUTES, SUBMIT_MOMENT_DATE_YMD } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { AppointmentTableItem } from '@/types/appointment';
import { APPOINTMENT_STATUS_TYPE } from '@/types/enums';
import { GetAllAppointmentRequest } from '@/types/http-payload/appointment';
import { formatDateTime } from '@/utils/datetime';
import { formatNumber, scrollToTop } from '@/utils/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { ColumnDef, TableState, Updater } from '@tanstack/react-table';
import Table from '@/components/table/Table';
import { SearchIcon } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InforIcon } from '@/components/icons';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { toJS } from 'mobx';

export default observer(function AppointmentPage() {
    // hooks
    const { t } = useTranslation();

    // store
    const { appointmentStore: { appointmentList, getListAppointment, paging, totalByStatus, totalRecord, setObservable, keepActivedTab, keepSearchParams, defaultPaging } } = useStore();

    // validate schema
    const validateSchema = yup.object().shape({
        appointmentCode: yup.string(),
        shopName: yup.string(),
        petOwnerName: yup.string(),
        fromAppointmentDate: yup.date().nullable(),
        toAppointmentDate: yup.date().nullable(),
        fromCreatedDate: yup.date().nullable(),
        toCreatedDate: yup.date().nullable()
    });

    type FormData = yup.InferType<typeof validateSchema>;

    // state
    const [activeTab, setActiveTab] = useState(keepActivedTab ? keepActivedTab : '');
    const [searchState, setSearchState] = useState<Object>(keepSearchParams ?? '');

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        defaultValues: {
            appointmentCode: '',
            shopName: '',
            petOwnerName: '',
            fromAppointmentDate: null,
            toAppointmentDate: null,
            fromCreatedDate: null,
            toCreatedDate: null
        }
    });

    const watchFromAppointmentTime = form.watch('fromAppointmentDate');
    const watchToAppointmentTime = form.watch('toAppointmentDate');
    const watchFromCreateDate = form.watch('fromCreatedDate');
    const watchToCreateDate = form.watch('toCreatedDate');

    // lifecycle
    useEffect(() => {
        if (keepSearchParams) {
            form.reset(keepSearchParams);
            onFetch(keepSearchParams, paging);
        }
    }, []);

    useEffect(() => {
        onSearch(searchState);
    }, [activeTab]);

    // function
    const onFetch = (searchParams?: any, paging?: TableState) => {
        if (paging) {
            setObservable('paging', paging ?? null);
        }

        setObservable('keepActivedTab', activeTab);
        setObservable('keepSearchParams', toJS(searchParams));
        getListAppointment(convertFormDataToSearchParams(searchParams), paging);
    };
    const onSearch = (searchParams: FormData) => {
        setSearchState(searchParams);
        onFetch(searchParams, defaultPaging);
    };

    const onStateChange = (stateUpdater: Updater<TableState>) => {
        const newPaging = stateUpdater instanceof Function ? stateUpdater(paging) : stateUpdater;
        onFetch(searchState, newPaging);
        scrollToTop();
    };

    const handleTabChange = (tabValue: string) => {
        setActiveTab(tabValue);
    };

    const convertFormDataToSearchParams = (data: FormData): GetAllAppointmentRequest => {
        return {
            ...data,
            fromAppointmentDate: formatDateTime(data.fromAppointmentDate, SUBMIT_MOMENT_DATE_YMD) || '',
            toAppointmentDate: formatDateTime(data.toAppointmentDate, SUBMIT_MOMENT_DATE_YMD) || '',
            fromCreatedDate: formatDateTime(data.fromCreatedDate, SUBMIT_MOMENT_DATE_YMD) || '',
            toCreatedDate: formatDateTime(data.toCreatedDate, SUBMIT_MOMENT_DATE_YMD) || '',
            status: activeTab
        };
    };

    const handleDisableTime = (date: Date, limitDate: Date | null | undefined, isToDate: boolean) => {
        if (limitDate) {
            return isToDate ? date < limitDate : date > limitDate;
        }
        return false;
    };

    // columns
    const columns: ColumnDef<AppointmentTableItem>[] = [
        {
            header: t('words_title.appointment_id'),
            size: 200,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div>{original.code}</div>
            )
        },
        {
            header: t('words_title.appointment_time'),
            size: 200,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div>{formatDateTime(`${original.appointmentDate} ${original.appointmentTime}`, DISPLAY_MOMENT_DATE_TIME_HM)}</div>
            )
        },
        {
            header: t('words_title.shop_name'),
            size: 200,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div>
                    {
                        original?.shop?.isDeleted ?
                            (
                                <div>{original?.shop?.name ?? ''}</div>
                            ) :
                            (
                                <a className='underline text-link-1 cursor-pointer' target='_blank' href={`${ROUTES.shopManagement.href}/${original.shop.code}`} rel='noreferrer'>{original?.shop?.name ?? ''}</a>
                            )
                    }
                </div>
            )
        },
        {
            header: t('words_title.pet_owner'),
            size: 200,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div>
                    {
                        original?.petOwner?.isDeleted ?
                            (
                                <div>{original?.petOwner?.fullName ?? ''}</div>
                            ) :
                            (
                                <a className='underline text-link-1 cursor-pointer' target='_blank' href={`/users/${original.petOwner.code}`} rel='noreferrer'>{original?.petOwner?.fullName ?? ''}</a>
                            )
                    }
                </div>

            )
        },
        {
            header: t('words_title.number_booked_service'),
            size: 120,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div>{formatNumber(original.numberSelectedServices)}</div>
            )
        },
        {
            header: `${t('words_title.total_amount')} (VND)`,
            size: 180,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div>
                    {formatNumber(original.totalAmount)}
                </div>
            )
        },
        {
            header: t('words_title.appointment_created_date'),
            size: 200,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div>{formatDateTime(original.createdAt, DATE_YMD_HMS_12H_FORMAT)}</div>
            )
        },
        {
            header: t('words_title.status'),
            size: 150,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='flex items-center justify-start gap-2'>
                    <div className={classNames('w-2 h-2 rounded-full flex-none', APPOINTMENT_STATUS[original.status].color)}></div>
                    <div>{t(`words_title.${APPOINTMENT_STATUS[original.status].key}`)}</div>
                </div>
            )
        },
        {
            id: 'action',
            size: 80,
            cell: ({ row: { original } }) => (
                <Link title={t('button.view_detail')} to={`${ROUTES.appointmentManagement.href}/${original.code}`} className='size-8 border border-active rounded-md flex justify-center items-center cursor-pointer'>
                    <InforIcon className='size-6 fill-active'></InforIcon>
                </Link>
            )
        }
    ];

    return (
        <div className='pb-4'>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSearch)}>
                    <div className='bg-white rounded p-4'>
                        <div className='grid grid-cols-2 gap-x-20 gap-y-4'>
                            <FormField
                                control={form.control}
                                name='appointmentCode'
                                render={({ field }) => (
                                    <FormItem className='flex gap-2 items-center'>
                                        <FormLabel className='w-1/5'>{t('words_title.appointment_id')}</FormLabel>
                                        <FormControl className='w-4/5'>
                                            <Input type='text' {...field} />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                            <div className='flex gap-3 items-center'>
                                <FormLabel className='w-1/5'>{t('words_title.appointment_time')}</FormLabel>
                                <FormField
                                    control={form.control}
                                    name='fromAppointmentDate'
                                    render={({ field }) => (
                                        <FormItem className='w-2/5'>
                                            <FormControl>
                                                <Calendar
                                                    mode='single'
                                                    selected={field.value}
                                                    onSelect={field.onChange}
                                                    disabled={date => handleDisableTime(date, watchToAppointmentTime, false)}
                                                    useIconRemove={true}
                                                    className='w-full'
                                                    placeholder={t('placeholder.from')}
                                                    required
                                                />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                                <div> - </div>
                                <FormField
                                    control={form.control}
                                    name='toAppointmentDate'
                                    render={({ field }) => (
                                        <FormItem className='w-2/5'>
                                            <FormControl>
                                                <Calendar
                                                    mode='single'
                                                    selected={field.value}
                                                    onSelect={field.onChange}
                                                    disabled={date => handleDisableTime(date, watchFromAppointmentTime, true)}
                                                    useIconRemove={true}
                                                    className='w-full'
                                                    placeholder={t('placeholder.to')}
                                                    required
                                                />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                            </div>
                            <FormField
                                control={form.control}
                                name='shopName'
                                render={({ field }) => (
                                    <FormItem className='flex gap-2 items-center'>
                                        <FormLabel className='w-1/5'>{t('words_title.shop_name')}</FormLabel>
                                        <FormControl className='w-4/5'>
                                            <Input type='text' {...field} />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                            <div className='flex gap-3 items-center'>
                                <FormLabel className='w-1/5'>{t('words_title.appointment_created_date')}</FormLabel>
                                <FormField
                                    control={form.control}
                                    name='fromCreatedDate'
                                    render={({ field }) => (
                                        <FormItem className='w-2/5'>
                                            <FormControl>
                                                <Calendar
                                                    mode='single'
                                                    selected={field.value}
                                                    onSelect={field.onChange}
                                                    disabled={date => handleDisableTime(date, watchToCreateDate, false)}
                                                    useIconRemove={true}
                                                    className='w-full'
                                                    placeholder={t('placeholder.from')}
                                                    required
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <div> - </div>
                                <FormField
                                    control={form.control}
                                    name='toCreatedDate'
                                    render={({ field }) => (
                                        <FormItem className='w-2/5'>
                                            <FormControl>
                                                <Calendar
                                                    mode='single'
                                                    selected={field.value}
                                                    onSelect={field.onChange}
                                                    disabled={date => handleDisableTime(date, watchFromCreateDate, true)}
                                                    useIconRemove={true}
                                                    className='w-full'
                                                    placeholder={t('placeholder.to')}
                                                    required
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                            <FormField
                                control={form.control}
                                name='petOwnerName'
                                render={({ field }) => (
                                    <FormItem className='flex gap-2 items-center'>
                                        <FormLabel className='w-1/5'>{t('words_title.pet_owner')}</FormLabel>
                                        <FormControl className='w-4/5'>
                                            <Input type='text' {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                        <div className='flex justify-center mt-8'>
                            <Button variant='outline' className='min-w-48 flex items-center justify-center gap-2 group'>
                                <SearchIcon className='w-5 h-5 text-active group-hover:text-white' />
                                <span>{t('button.search')}</span>
                            </Button>
                        </div>
                    </div>
                </form>
            </Form>

            <Tabs value={activeTab} onValueChange={handleTabChange} className='w-full h-full flex flex-col'>
                <div className='bg-white mt-4 rounded p-4'>
                    <TabsList variant='shop' data-orientation='horizontal' className='flex w-full mt-6'>
                        <TabsTrigger
                            variant='shop'
                            className='md:text-[0.875rem] text-3 w-full'
                            value=''
                        >
                            {`${t('words_title.all_appointment')} (${Object.values(totalByStatus).reduce((accumulator, value) => accumulator += value, 0)})`}
                        </TabsTrigger>
                        {
                            Object.entries([
                                APPOINTMENT_STATUS[APPOINTMENT_STATUS_TYPE.NotConfirmed],
                                APPOINTMENT_STATUS[APPOINTMENT_STATUS_TYPE.Confirmed],
                                APPOINTMENT_STATUS[APPOINTMENT_STATUS_TYPE.Completed],
                                APPOINTMENT_STATUS[APPOINTMENT_STATUS_TYPE.Cancelled]
                            ]).map(([key, value], index) => (
                                <TabsTrigger
                                    key={index}
                                    variant='shop'
                                    className='md:text-[0.875rem] text-3 w-full'
                                    value={value.value.toString()}
                                >
                                    {`${t(`words_title.${value.key}`)} (${totalByStatus?.[value.value] ?? '0'})`}
                                </TabsTrigger>
                            ))
                        }
                    </TabsList>
                </div>

                <div className='bg-white rounded p-4'>
                    <TabsContent value=''>
                        <Table
                            columns={columns}
                            data={appointmentList}
                            rowCount={totalRecord}
                            onStateChange={onStateChange}
                            state={paging}
                            selectPageSizes={DEFAULT_SELECT_PAGE_SIZES}
                            getRowId={row => row.id?.toString()}
                        />
                    </TabsContent>
                    {
                        Object.entries([
                            APPOINTMENT_STATUS[APPOINTMENT_STATUS_TYPE.NotConfirmed],
                            APPOINTMENT_STATUS[APPOINTMENT_STATUS_TYPE.Confirmed],
                            APPOINTMENT_STATUS[APPOINTMENT_STATUS_TYPE.Completed],
                            APPOINTMENT_STATUS[APPOINTMENT_STATUS_TYPE.Cancelled]
                        ]).map(([key, value], index) => (
                            <TabsContent value={value.value.toString()} key={index}>
                                <Table
                                    columns={columns}
                                    data={appointmentList}
                                    rowCount={totalRecord}
                                    onStateChange={onStateChange}
                                    state={paging}
                                    selectPageSizes={DEFAULT_SELECT_PAGE_SIZES}
                                    getRowId={row => row.id?.toString()}
                                />
                            </TabsContent>
                        ))
                    }
                </div>
            </Tabs>
        </div>
    );
});
